import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = {
    class: "flex flex-col items-center gap-2 text-center",
    "data-testid": "confirm-last-owner-body"
};
const _hoisted_2 = ["id"];
const _hoisted_3 = { class: "text-base leading-6 text-(--text-text-body) dark:text-(--text-text-white-default)" };
const _hoisted_4 = {
    key: 0,
    class: "mt-1 w-full rounded-lg bg-(--background-background-error) border border-(--border-border-error-subtle) px-4 py-3 text-sm text-(--text-text-error) flex items-center gap-2"
};
import { nextTick, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, IconShape, Modal } from '@eudonet/opal';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ConfirmLastSharedOwnerModal',
    props: /*@__PURE__*/ _mergeModels({
        name: {},
        mode: { default: 'edit' },
        submitting: { type: Boolean, default: false },
        serverError: { default: null }
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["confirm", "cancel"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        /**
         * "Le dernier contact deviendra principal" confirmation (OP-5913, follow-up to OP-5804 / US-04c).
         *
         * Shown when the PRIMARY owner of a shared email leaves the group (value change or email deletion)
         * and exactly ONE member would remain: OPHUB then auto-dissolves the group and the lone survivor
         * becomes the sole owner of the (now non-shared) email — `RemoveContactEmailFromGroupAsync` ignores
         * `newPrimaryChannelId` once `remaining.Count <= 1`. There is no choice to make (single candidate),
         * so this is a plain acknowledgement, NOT the {@link DesignatePrimaryContactModal} radio picker: the
         * write (`PUT`/`DELETE`) proceeds unchanged on confirm, carrying no transfer target.
         *
         * Reused across the edit flow (ContactEditEmailModal) and the email-delete flow (ContactDetail) via
         * the `mode` prop, which only swaps the title. The parent owns the persistence: it sets `submitting`
         * while the write is in flight and `serverError` (R70 errorCode → i18n) to keep the modal open on
         * failure — mirroring the DesignatePrimaryContactModal contract.
         *
         * NOT used by the contact-delete flow (OP-6527/OP-7215): a single-survivor shared-email group there
         * goes straight to the Path A/B confirmation with an "Étape 2 sur 2" subtitle — no separate
         * acknowledgement step (AC30) — so this modal never opens for that flow.
         */
        const TITLE_KEYS = {
            edit: 'editTitle',
            delete: 'deleteTitle',
        };
        const open = _useModel(__props, "modelValue");
        const emit = __emit;
        const { t } = useI18n();
        const tk = (key) => t(`contact.detail.confirmLastOwner.${key}`);
        const titleId = useId();
        const cancelButtonRef = ref(null);
        // Destructive-action safety (OP-6527 AC): focus lands on "Annuler" on open, not the confirm CTA.
        // nextTick() wins the race against Modal's rAF-deferred autofocus fallback (see useFocusTrap.ts).
        watch(open, async (isOpen) => {
            if (!isOpen)
                return;
            await nextTick();
            cancelButtonRef.value?.$el?.focus();
        });
        function onConfirm() {
            emit('confirm');
        }
        function onCancel() {
            open.value = false;
            emit('cancel');
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((open).value = $event)),
                size: "xs",
                dismissable: false,
                "close-on-escape": !__props.submitting,
                "show-header": false,
                "footer-position": "center",
                "aria-labelledby": _unref(titleId),
                onClose: onCancel
            }, {
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        ref_key: "cancelButtonRef",
                        ref: cancelButtonRef,
                        variant: "secondary",
                        size: "md",
                        label: tk('cancel'),
                        disabled: __props.submitting,
                        "data-testid": "confirm-last-owner-cancel",
                        onClick: onCancel
                    }, null, 8 /* PROPS */, ["label", "disabled"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        size: "md",
                        "icon-left": "ri-check-line",
                        label: tk('confirm'),
                        loading: __props.submitting,
                        disabled: __props.submitting,
                        "data-testid": "confirm-last-owner-confirm",
                        onClick: onConfirm
                    }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createVNode(_unref(IconShape), {
                            size: "md",
                            color: "orange",
                            variant: "outlined",
                            icon: "ri-user-shared-line"
                        }),
                        _createElementVNode("h2", {
                            id: _unref(titleId),
                            class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default) leading-6"
                        }, _toDisplayString(tk(TITLE_KEYS[__props.mode])), 9 /* TEXT, PROPS */, _hoisted_2),
                        _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)('contact.detail.confirmLastOwner.becomesPrimary', { name: __props.name })), 1 /* TEXT */),
                        (__props.serverError)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                _cache[1] || (_cache[1] = _createElementVNode("i", {
                                    class: "ri-error-warning-line text-base shrink-0",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */)),
                                _createTextVNode(" " + _toDisplayString(__props.serverError), 1 /* TEXT */)
                            ]))
                            : _createCommentVNode("v-if", true)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "close-on-escape", "aria-labelledby"]));
        };
    }
});
